import { classMerge } from '@components/utilities/classMerge'
import anime from 'animejs'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'

export interface AnimatedAccordionProps {
  children: ReactNode
  className?: string
  duration?: number
  easing?: string
  isExpanded: boolean
}

export const AnimatedAccordion = ({
  children,
  className,
  duration = 300,
  easing = 'easeOutQuart',
  isExpanded,
}: AnimatedAccordionProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = useState<number>(0)
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    if (contentRef.current) {
      const height = contentRef.current.scrollHeight
      setContentHeight(height)
      setIsInitialized(true)
    }
  }, [children])

  useEffect(() => {
    if (!containerRef.current || !isInitialized) return

    const initialHeight = isExpanded ? contentHeight : 0

    containerRef.current.style.height = `${initialHeight}px`
    containerRef.current.style.visibility = isExpanded ? 'visible' : 'hidden'
  }, [isInitialized])

  useEffect(() => {
    if (!containerRef.current || !isInitialized) return

    const container = containerRef.current
    const targetHeight = isExpanded ? contentHeight : 0

    // If collapsing, first capture the current height (which might be 'auto')
    if (!isExpanded && container.style.height === 'auto') {
      container.style.height = `${container.offsetHeight}px`
    }

    anime({
      begin: () => {
        // Ensure content is visible during expand animation
        if (isExpanded && container) {
          container.style.visibility = 'visible'
        }
      },
      complete: () => {
        if (!container) return

        if (!isExpanded) {
          // Hide content when fully collapsed
          container.style.visibility = 'hidden'
        } else {
          // Switch to auto height after expansion - allows natural content growth
          container.style.height = 'auto'
        }
      },
      duration,
      easing,
      height: targetHeight,
      targets: container,
    })
  }, [isExpanded, contentHeight, duration, easing, isInitialized])

  return (
    <div
      className={classMerge('overflow-hidden', !isExpanded && 'h-0', className)}
      ref={containerRef}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  )
}
